<template>
  <div class="changes col-xl-5 mx-auto mt-4">
    <h5>Ownership Changes in this <b>NJCRIB ERM-14</b> Form</h5>
    <p v-if="!changes.length">You don't have any changes. Click the button below to add one.</p>
    <div v-else>
      <b-table striped :items="changes" :fields="fields" class="table-light">
        <template v-slot:cell(changeType)="data">
          <i v-if="isReturned(data.item.submitted)" class="fa fa-exclamation-triangle text-danger mr-1" v-b-tooltip="{title: `This change was returned by NJCRIB. Click 'Correspondence' to see their comments.`, class:'danger'}"></i>{{ changeTypeDescription(data.item.changeTypeID) }}
        </template>
        <template v-slot:cell(actions)="data">
          <b-button
            :to="{ name: 'change/edit', params: { submissionId: submission.submissionID, changeId: data.item.changeID }}"
            size="sm"
            variant="success">{{ canEdit(data.item.changeID) ? 'Edit' : 'View' }}</b-button>
          <b-button 
            @click.prevent.stop="deleteClicked(data.item.changeID)" 
            variant="danger" 
            size="sm" 
            class="ml-1"
            v-if="canDelete(data.item.changeID)">Delete</b-button>
          <b-button
            variant="secondary"
            size="sm"
            class="ml-1"
            @click="downloadCorrespondence(data.item.changeID)"
            v-if="isReturned(data.item.submitted) && correspondences(data.item.changeID).length">
            Correspondence
          </b-button>
        </template>
      </b-table>
    </div>
    <b-button
      :to="{ name: 'change/edit', params: { submissionId: submission.submissionID } }"
      variant="success"
      v-if="canAddNewChanges"
    >{{ this.changes.length ? 'Add Additional Ownership Change' : 'Add Ownership Change' }}</b-button>
    <b-alert show variant="danger" v-if="submissionErrors.length" class="mt-5 text-left">
      <h4 class="mb-0">Error{{ submissionErrors.length == 1 ? '' : 's' }}</h4>
      <hr class="mt-1">
        <div v-for="(ce, i) in submissionErrors" :key="i">
          <h5>{{ ce.section }}</h5>
          <ul>
            <li v-for="(e, j) in ce.errors" :key="j">
              {{ e }}
            </li>
          </ul>
        </div>
    </b-alert>

    <b-modal ref="delete-modal" title="Are you sure?" header-text-variant="dark" body-text-variant="dark" centered>
      <template slot="default">
        Are you sure you want to delete this ownership change?
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="danger" @click.prevent="deleteChange" :disabled="isDeleting">
          <span v-if="!isDeleting">Delete</span>
          <span v-else><b-spinner small></b-spinner> Please Wait...</span>
        </b-button>
        <b-button variant="secondary" @click.prevent="cancelClicked">Cancel</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import SubmissionService from '../services/SubmissionService.js'
import BaseService from '../services/BaseService.js'

export default {
  name: 'Changes',
  data() {
    return {
      isDeleting: false,
      submissionErrors: [],
      fields: [
        { key: 'changeType' , label: 'Type', sortable: true, thStyle: { width: '55%' } },
        { key: 'effectiveDate' , label: 'Effective Date', sortable: true, thStyle: { width: '15%' }, formatter: this.DateFormatter },
        { key: 'reportedDate' , label: 'Reported Date', sortable: true, thStyle: { width: '15%' }, formatter: this.DateFormatter },
        { key: 'actions', label: '', sortable: false, class: ['action-buttons'] }
      ],
      changeIdToDelete: ''
    }
  },
  methods: {
    canEdit(id) {
      return !this.$store.getters['application/isChangeByChangeIdViewOnly'](id)    
    },
    canDelete(id) {
      return !this.$store.getters['application/isChangeByChangeIdViewOnly'](id) && this.submission.statusID != 3
    },
    correspondences(id) {
      return this.$store.state.document.documents.filter(d => d.changeID == id && d.syncID)
    },
    downloadCorrespondence(id) {
      const documentID = this.correspondences(id)[0].documentID
      this.error = false
      BaseService.getFileDownload(`/Document/${documentID}`).then(response => {
        this.DownloadFile(response)
      }).catch(() => {
        this.error = true
      })
    },
    isReturned(isSubmitted) {
      return this.submission.statusID == 3 && !isSubmitted
    },
    changeTypeDescription(id) {
      return this.changeTypes.find(c => c.changeTypeID == id).shortDescription
    },
    deleteClicked(id) {
      this.$refs['delete-modal'].show()
      this.changeIdToDelete = id
    },
    cancelClicked() {
      this.$refs['delete-modal'].hide()
      this.changeIdToDelete = ''
    },
    deleteChange() {
      this.isDeleting = true
      this.$store.dispatch('change/delete', this.changeIdToDelete).then(() => {
        this.isDeleting = false
        this.$refs['delete-modal'].hide()
        this.changeIdToDelete = ''
      }).catch(() => {
        this.isDeleting = false
      })
    },
    backClicked() {
      this.$router.push({
        name: 'submission/list'
      })
    },
    nextClicked() {
      if (this.isSubmissionViewOnly) {
        this.$router.push({
          name: 'review',
          params: {
            submissionId: this.submission.submissionID
          }
        })
      } else {      
        SubmissionService.validate(this.submission.submissionID).then(() => {
          this.$router.push({
            name: 'review',
            params: {
              submissionId: this.submission.submissionID
            }        
          })
        }).catch(res => {
          this.submissionErrors = res.response.data
        })
      }
    }
  },
  computed: {
    ...mapState({
      changes: state => state.change.changes.sort((a, b) => a.changeID - b.changeID),
      submission: state => state.submission.submission,
      changeTypes: state => state.application.changeTypes
    }),
    ...mapGetters('application', ['isSubmissionViewOnly']),
    canAddNewChanges() {
      return !this.isSubmissionViewOnly && this.submission.statusID != 3
    },
  },
  created() {
    this.$EventBus.$on('back-clicked', this.backClicked)
    this.$EventBus.$on('next-clicked', this.nextClicked)
  },
  beforeDestroy() {
    this.$EventBus.$off('back-clicked', this.backClicked)
    this.$EventBus.$off('next-clicked', this.nextClicked)
  }
}
</script>